import React, { useState, useEffect, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
    Box, Paper,
    Typography,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    LinearProgress,
    Tabs,
    Tab,
    TextField,
    Avatar,
    Button,
    Divider,
    IconButton,
    Tooltip,
    Menu, Stack,
    MenuItem, Grid, TableHead, TableContainer, Snackbar, Alert,
    Modal, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select
} from '@mui/material';
import {
    Code, Flag, CheckCircle, StackedBarChart, AssignmentInd, Person, People, CheckCircle as CheckCircleIcon,
    Business, Close as CloseIcon,
    Subject as SubjectIcon,
    Person as PersonIcon,
    PlaylistAddCheck as PlaylistAddCheckIcon,
    Timeline as TimelineIcon,
    CalendarToday as CalendarTodayIcon,
    Comment as CommentIcon,
    Add as AddIcon,
    Visibility as VisibilityIcon,
    AccessTime as AccessTimeIcon,
    AttachFile as AttachFileIcon,
    Description as DescriptionIcon,
    Lock as LockIcon,
    Delete as DeleteIcon,
    TaskAlt, Subject, Comment,
    ArrowDropDown, AccountCircle, Edit as EditIcon, AccessAlarm, AccessTimeFilled
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { styled } from '@mui/material/styles';
import Back from '../assets/Back.png';
import { EditNote as EditTaskIcon } from '@mui/icons-material';
import moment from 'moment';
import CreateTask from '../pages/Manager/Epics/CreateTask';

const statusColors = {
    'Open': 'info',
    'In Progress': 'primary',
    'Submit For Review': 'warning',
    'Completed': 'success',
    'Re Open': 'secondary',
    'Hold': 'default',
    'Overdue': 'error',
    'default': 'default'
};

const statusTextColors = {
    'Open': '#ffffff',
    'In Progress': '#ffffff',
    'Submit For Review': '#ffffff',
    'Completed': '#ffffff',
    'Re Open': '#ffffff',
    'Hold': '#757575',
    'Overdue': '#ffffff',
    'default': '#757575',
};

const priorityColors = {
    'High': 'error',
    'Medium': 'warning',
    'Low': 'success',
    'default': 'default',
};

const priorityTextColors = {
    'High': '#ffffff',
    'Medium': '#ffffff',
    'Low': '#ffffff',
    'default': '#757575',
};

const workTypes = ['Development', 'Enhancement', 'Bug', 'Feature', 'Testing', 'Documentation'];

const stripedRowColors = {
    odd: '#f0f0f0',
    even: '#fff',
};

const StripedTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '& *': {
        padding: theme.spacing(1),
    },
}));

const formatTime = (hoursDecimal) => {
    if (hoursDecimal === null || hoursDecimal === undefined || hoursDecimal === '') {
        return '--:--';
    }

    // Convert decimal hours to total minutes
    const totalMinutes = hoursDecimal * 60;
    let hours = Math.floor(totalMinutes / 60);
    let minutes = Math.round(totalMinutes % 60);

    // Handle case where minutes round up to 60
    if (minutes === 60) {
        hours += 1;
        minutes = 0;
    }

    return `${hours} hrs ${minutes.toString().padStart(2, '0')} mins`;
}

// Dummy data for task details
const dummyTaskDetails = {
    task_id: 'TASK-001',
    task_title: 'Implement User Authentication System',
    description: 'Create a secure user authentication system with email verification, password reset functionality, and role-based access control. Include features like login, registration, and session management.',
    task_type: 'Multiple', // Can be 'Single' or 'Multiple'
    work_type: 'Development', // Changed from development_type to work_type
    project_name: 'E-Commerce Platform',
    manager_first_name: 'John',
    manager_last_name: 'Smith',
    start_date: '2024-01-01',
    due_date: '2024-01-31',
    estimated_hours: 40,
    actual_hours: 35,
    progress: 75,
    status: 'In Progress',
    priority: 'High',
    submit_for_review: false,
    attachments: [
        {
            file_url: '#',
            file_name: 'auth_specs.pdf',
        },
        {
            file_url: '#',
            file_name: 'database_schema.png',
        }
    ],
    assignees: [
        { first_name: 'Alice', last_name: 'Johnson', color: '#ff5722' },
        { first_name: 'Bob', last_name: 'Williams', color: '#4caf50' }
    ],
    comments: [
        {
            id: 1,
            comment: 'Started working on authentication middleware',
            user_type: 'Manager',
            user: { first_name: 'John', last_name: 'Smith' },
            createdAt: '2024-01-10T10:30:00'
        },
        {
            id: 2,
            comment: 'Database schema for users is ready',
            user_type: 'Employee',
            user: { first_name: 'Alice', last_name: 'Johnson' },
            createdAt: '2024-01-12T14:20:00'
        }
    ],
    subtasks: [
        {
            sub_task_id: 'SUB-001',
            task_title: 'Design Database Schema',
            status: 'Completed',
            assignees: [{ first_name: 'Alice', last_name: 'Johnson' }],
            submit_for_review: true,
            work_type: 'Development'
        },
        {
            sub_task_id: 'SUB-002',
            task_title: 'Implement Login API',
            status: 'In Progress',
            assignees: [{ first_name: 'Bob', last_name: 'Williams' }],
            submit_for_review: false,
            work_type: 'Development'
        },
        {
            sub_task_id: 'SUB-003',
            task_title: 'Fix Login Button Alignment',
            status: 'To Do',
            assignees: [{ first_name: 'Charlie', last_name: 'Brown' }],
            submit_for_review: false,
            work_type: 'Bug'
        }
    ]
};

const TaskDetailsDummy = ({ onClose, taskId, onTaskDeleted }) => {
    const [value, setValue] = useState(0);
    const [taskDetails, setTaskDetails] = useState(dummyTaskDetails);
    const [statusAnchorEl, setStatusAnchorEl] = useState(null);
    const [priorityAnchorEl, setPriorityAnchorEl] = useState(null);
    const [updatedTaskDetails, setUpdatedTaskDetails] = useState({
        progress: dummyTaskDetails.progress,
        status: dummyTaskDetails.status,
        priority: dummyTaskDetails.priority,
        comment: '',
        submit_for_review: dummyTaskDetails.submit_for_review,
        actual_time: dummyTaskDetails.actual_hours,
    });

    const [selectedSubTaskId, setSelectedSubTaskId] = useState(null);
    const [isEditTaskDetailsOpen, setIsEditTaskDetailsOpen] = useState(false);
    const [viewingSubTask, setViewingSubTask] = useState(false);
    const [selectedTask, setSelectedTask] = useState({});
    const [addSubtaskModalOpen, setAddSubtaskModalOpen] = useState(false);

    const [userDetails, setUserDetails] = useState('');
    const [isManager, setIsManager] = useState(true); // Set to true for demo
    const [taskComments, setTaskComments] = useState(dummyTaskDetails.comments);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');

    const [openEditModal, setOpenEditModal] = useState(false);
    const [actualTimeEdit, setActualTimeEdit] = useState({
        hours: '',
        minutes: ''
    });
    const [minutesError, setMinutesError] = useState(false);

    const [attachments, setAttachments] = useState([]);
    const [openPreview, setOpenPreview] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [taskApiMessage, setTaskApiMessage] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
    const [isCreatingSubtask, setIsCreatingSubtask] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (updatedTaskDetails.status === 'Completed') {
            setUpdatedTaskDetails({
                ...updatedTaskDetails,
                progress: 100,
                status: 'Completed'
            });
        }
    }, [updatedTaskDetails.status]);

    useEffect(() => {
        const storedUserDetails = localStorage.getItem('userDetails');
        if (storedUserDetails) {
            try {
                const parsedUserDetails = JSON.parse(storedUserDetails);
                setUserDetails(parsedUserDetails);
                setIsManager(parsedUserDetails.user_type === 'Manager' || parsedUserDetails.user_type === 'Team Lead');
            } catch (error) {
                console.error('Error parsing userDetails from localStorage:', error);
                setIsManager(true); // Default to manager for demo
            }
        } else {
            setIsManager(true); // Default to manager for demo
        }
    }, []);

    const openStatusMenu = (event) => {
        setStatusAnchorEl(event.currentTarget);
    };

    const closeStatusMenu = () => {
        setStatusAnchorEl(null);
    };

    const openPriorityMenu = (event) => {
        setPriorityAnchorEl(event.currentTarget);
    };

    const closePriorityMenu = () => {
        setPriorityAnchorEl(null);
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleSubTaskDetails = (subTaskId) => {
        setSelectedSubTaskId(subTaskId);
        setViewingSubTask(true);
    };

    const handleAddSubtask = () => {
        setIsCreatingSubtask(true);
        setIsCreateTaskOpen(true);
    };

    const handleCloseCreateTask = (createdTask = null) => {
        setIsCreateTaskOpen(false);
        setIsCreatingSubtask(false);

        if (createdTask) {
            // Refresh task details or add new subtask to the list
            // fetchTaskDetails(); // You'll need to implement this
        }
    };

    const handleAddSubtaskSubmit = (newSubtask) => {
        setTaskDetails(prev => ({
            ...prev,
            subtasks: [...prev.subtasks, newSubtask]
        }));
        setSnackbarMessage('Subtask added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleDeleteSubtask = (subTaskId) => {
        setTaskDetails(prev => ({
            ...prev,
            subtasks: prev.subtasks.filter(subtask => subtask.sub_task_id !== subTaskId)
        }));
        setSnackbarMessage('Subtask deleted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleBackFromSubTask = () => {
        setViewingSubTask(false);
        setSelectedSubTaskId(null);
    };

    const handleEditTaskDetails = (task) => {
        console.log('Editing task ID:', task);
        setIsEditTaskDetailsOpen(true);
        setSelectedTask(task);
    };

    const handleEditTaskPopupClose = () => {
        setIsEditTaskDetailsOpen(false);
    };

    function capitalizeFirstChar(str) {
        if (!str) {
            return "";
        }
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    const handleOpenEditModal = () => {
        const existingTime = updatedTaskDetails.actual_time || taskDetails.actual_hours || '';
        if (existingTime) {
            const parts = String(existingTime).split('.');
            setActualTimeEdit({
                hours: parts[0] || '',
                minutes: parts[1] || ''
            });
        } else {
            setActualTimeEdit({ hours: '', minutes: '' });
        }
        setMinutesError(false);
        setOpenEditModal(true);
    };

    const handleCloseEditModal = () => {
        setOpenEditModal(false);
        setActualTimeEdit({ hours: '', minutes: '' });
        setMinutesError(false);
    };

    const handleActualTimeHoursChange = (e) => {
        const value = e.target.value;
        if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
            setActualTimeEdit(prev => ({ ...prev, hours: value }));
        }
    };

    const handleActualTimeMinutesChange = (e) => {
        const value = e.target.value;
        if (value === '' || (/^\d+$/.test(value))) {
            const numValue = parseInt(value);
            if (value === '' || numValue <= 59) {
                setActualTimeEdit(prev => ({ ...prev, minutes: value }));
                setMinutesError(false);
            } else {
                setMinutesError(true);
            }
        }
    };

    const handleSaveActualTime = () => {
        const { hours, minutes } = actualTimeEdit;

        if (!hours && !minutes) {
            handleCloseEditModal();
            return;
        }

        if (parseInt(minutes) > 59) {
            setMinutesError(true);
            return;
        }

        const minutesDecimal = minutes ? parseInt(minutes) / 60 : 0;
        const hoursValue = hours ? parseInt(hours) : 0;
        const formattedTime = hoursValue + minutesDecimal;

        setUpdatedTaskDetails(prev => ({
            ...prev,
            actual_time: formattedTime
        }));

        handleCloseEditModal();
    };

    const handleSendForReviewButton = async () => {
        setIsLoading(true);
        setTaskApiMessage('');

        if (updatedTaskDetails.actual_time === '') {
            setTaskApiMessage('Please update the Actual time for task');
            setIsLoading(false);
            return;
        }

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setTaskApiMessage('Task submitted for review successfully!');

            // Update task details
            setTaskDetails(prev => ({
                ...prev,
                submit_for_review: true,
                actual_hours: updatedTaskDetails.actual_time
            }));

        } catch (error) {
            console.error('Submission failed:', error);
            setTaskApiMessage('Failed to submit for review. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveUpdateDetailsButton = async () => {
        setIsLoading(true);

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Update task details locally
            setTaskDetails(prev => ({
                ...prev,
                progress: updatedTaskDetails.progress,
                status: updatedTaskDetails.status,
                priority: updatedTaskDetails.priority
            }));

            setTaskApiMessage('Task Details are updated successfully!');
        } catch (error) {
            console.error('Update failed:', error);
            setTaskApiMessage('Failed to save the details. Please try again.');
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 1000);
        }
    };

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);

        const newAttachments = files.map(file => ({
            id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file: file,
            name: file.name,
            type: file.type,
            url: URL.createObjectURL(file)
        }));

        setAttachments([...attachments, ...newAttachments]);
        event.target.value = null;
    };

    const handleOpenPreview = (attachment) => {
        setPreviewFile(attachment);
        setOpenPreview(true);
    };

    const handleClosePreview = () => {
        setOpenPreview(false);
        setPreviewFile(null);
    };

    const handleDeleteAttachment = (id) => {
        const updatedAttachments = attachments.filter(attachment => attachment.id !== id);
        setAttachments(updatedAttachments);
    };

    const handleDeleteClick = (taskId) => {
        setTaskToDelete(taskId);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!taskToDelete) return;

        try {
            // Simulate delete API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setSnackbarOpen(true);
            setSnackbarMessage('Successfully Deleted the Task');
            setSnackbarSeverity('success');

            setTimeout(() => {
                if (onTaskDeleted) {
                    onTaskDeleted();
                }
                if (onClose) {
                    onClose();
                }
            }, 1500);
        } catch (error) {
            console.error('Error in deleting task', error);
            setSnackbarOpen(true);
            setSnackbarMessage('Error while deleting the Task');
            setSnackbarSeverity('error');
        } finally {
            setDeleteConfirmOpen(false);
            setTaskToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setTaskToDelete(null);
    };

    const UploadedFilesList = () => {
        const [openPreview, setOpenPreview] = useState(false);
        const [previewFile, setPreviewFile] = useState(null);

        const allFiles = [
            ...(taskDetails.attachments?.map(attachment => ({
                url: attachment.file_url,
                name: attachment.file_name,
                type: attachment.file_name?.match(/\.(jpeg|jpg|png|gif)$/i) ? 'image' :
                    attachment.file_name?.endsWith('.pdf') ? 'pdf' : 'file'
            })) || [])
        ];

        if (allFiles.length === 0) return null;

        const isImageFile = (filename) => {
            return filename?.match(/\.(jpeg|jpg|png|gif)$/i);
        };

        const handleOpenPreview = (file) => {
            setPreviewFile(file);
            if (isImageFile(file.name)) {
                setOpenPreview(true);
            } else {
                window.open(file.url, '_blank', 'noopener,noreferrer');
            }
        };

        const handleClosePreview = () => setOpenPreview(false);

        const handleDownload = async (file) => {
            try {
                if (!file.url) return;
                const link = document.createElement('a');
                link.href = file.url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error('Download failed:', error);
                window.open(file.url, '_blank');
            }
        };

        return (
            <>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: 'rgba(100, 181, 246, 0.1)',
                        padding: '8px',
                        borderBottom: '1px solid #ccc',
                    }}
                >
                    <CloudUploadIcon color="primary" />
                    <Typography variant="subtitle2" color="primary" fontWeight={600}>
                        Uploaded files ({allFiles.length})
                    </Typography>
                </Box>

                <TableContainer component={Paper} elevation={2}>
                    <Table size="small">
                        <TableBody>
                            {allFiles.map((file, index) => (
                                <TableRow key={`${file.name}-${index}`}>
                                    <TableCell sx={{ width: 40 }}>
                                        {isImageFile(file.name) ? (
                                            <ImageIcon color="primary" />
                                        ) : file.name.endsWith('.pdf') ? (
                                            <PictureAsPdfIcon color="error" />
                                        ) : (
                                            <InsertDriveFileIcon color="action" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                            {file.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 120 }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Preview">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenPreview(file)}
                                                    color="primary"
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Download">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDownload(file)}
                                                    color="primary"
                                                >
                                                    <CloudDownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {openPreview && previewFile && isImageFile(previewFile.name) && (
                    <Dialog
                        open={openPreview}
                        onClose={handleClosePreview}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>{previewFile.name}</DialogTitle>
                        <DialogContent sx={{ textAlign: 'center' }}>
                            <img
                                src={previewFile.url}
                                alt={previewFile.name}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain'
                                }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                startIcon={<CloudDownloadIcon />}
                                onClick={() => handleDownload(previewFile)}
                                variant='contained'
                            >
                                Download
                            </Button>
                            <Button variant='outlined' onClick={handleClosePreview}>Close</Button>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    };

    const renderFileIcon = (fileType) => {
        if (fileType.startsWith('image/')) {
            return <ImageIcon />;
        } else if (fileType === 'application/pdf') {
            return <PictureAsPdfIcon />;
        } else {
            return <InsertDriveFileIcon />;
        }
    };

    const getWorkTypeColor = (workType) => {
        switch (workType) {
            case 'Development': return '#1976d2';
            case 'Enhancement': return '#9c27b0';
            case 'Bug': return '#d32f2f';
            case 'Feature': return '#4caf50';
            case 'Testing': return '#f57c00';
            case 'Documentation': return '#607d8b';
            default: return '#757575';
        }
    };

    if (viewingSubTask && selectedSubTaskId) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f0f4f8', borderRadius: '12px' }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', p: 2 }}>
                    <img
                        src={Back}
                        alt="Back"
                        style={{
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease'
                        }}
                        onClick={handleBackFromSubTask}
                    />
                    <Typography variant="subtitle1" sx={{ ml: 2, fontWeight: 'medium' }}>
                        Back to Main Task
                    </Typography>
                </Box>
                <TaskDetailsDummy onClose={handleBackFromSubTask} taskId={selectedSubTaskId} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', bgcolor: '#f0f4f8', borderRadius: '12px' }}>
            <Box sx={{ flexGrow: 1, overflow: 'auto', borderRadius: '12px', p: 2 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '12px',
                    bgcolor: 'white'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        p: 2,
                        borderRadius: '4px',
                        color: 'white',
                        justifyContent: 'space-between',
                        width: '100%'
                    }}>
                        <Typography variant="h5" component="span" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TaskAlt />
                            Task Details
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', mt: 2, gap: 2 }}>
                    <Box sx={{ p: 3, bgcolor: '#ffffff', flexGrow: 1, display: 'flex' }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        variant="h6"
                                        color="primary"
                                        sx={{ fontWeight: 'bold', color: '#2e86de' }}
                                    >
                                        #{taskDetails.task_id}
                                    </Typography>
                                    <Typography variant="h6" sx={{ ml: 1 }}>
                                        {taskDetails.task_title}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {/* Display Multiple/Single badge */}
                                   
                                    {taskDetails.submit_for_review && (
                                        <Chip
                                            label={isManager ? "Ready for Review" : "Send for Review"}
                                            size="small"
                                            variant="contained"
                                            sx={{ backgroundColor: '#FFBF00', color: 'white' }}
                                        />
                                    )}
                                    {isManager && (
                                        <Box>
                                            <IconButton aria-label="edit"
                                                size="small"
                                                sx={{ color: 'orange' }}
                                                onClick={() => handleEditTaskDetails(taskDetails)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton aria-label="delete" size="small" color="error"
                                                onClick={() => handleDeleteClick(taskDetails.task_id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    {taskDetails.task_type} Task •{' '}
                                    <Chip
                                        label={taskDetails.work_type}
                                        size="small"
                                        sx={{
                                            backgroundColor: getWorkTypeColor(taskDetails.work_type),
                                            color: 'white',
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Typography>
                            </Box>
                            <Box mt={2} bgcolor="#f8fafc" p={2} borderRadius={1}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Business sx={{ color: '#1976d2' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Project Name
                                            </Typography>
                                            <Chip label={taskDetails?.project_name || '-'} size="small" />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon sx={{ color: '#4caf50' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Created By
                                            </Typography>
                                            <Chip label={`${capitalizeFirstChar(taskDetails?.manager_first_name)} ${capitalizeFirstChar(taskDetails?.manager_last_name)}` || 'N/A'} size="small" />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {taskDetails?.work_type ? <Code sx={{ color: '#f57c00' }} /> : <AssignmentInd sx={{ color: '#2e7d32' }} />}
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Work Type
                                            </Typography>
                                            <Chip
                                                label={taskDetails?.work_type || '-'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getWorkTypeColor(taskDetails.work_type),
                                                    color: 'white'
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TimelineIcon sx={{ color: '#9c27b0' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Timeline
                                            </Typography>
                                            <Chip
                                                label={taskDetails?.start_date ? new Date(taskDetails.start_date).toLocaleDateString() : '-'}
                                                size="small"
                                            />
                                            -
                                            <Chip
                                                label={taskDetails?.due_date ? new Date(taskDetails.due_date).toLocaleDateString() : '-'}
                                                size="small"
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccessAlarm sx={{ color: '#800080' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Estimated Time
                                            </Typography>
                                            <Chip label={formatTime(taskDetails?.estimated_hours) || '-'} size="small" />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccessTimeFilled sx={{ color: '#2e7d32' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Actual Time
                                            </Typography>
                                            <Chip
                                                label={formatTime(
                                                    updatedTaskDetails.actual_time || taskDetails?.actual_hours
                                                )}
                                                size="small"
                                            />

                                            {!isManager && (
                                                <IconButton
                                                    aria-label="edit actual time"
                                                    size="small"
                                                    onClick={handleOpenEditModal}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    marginBottom: '16px',
                                    mt: 2,
                                    bgcolor: '#f8fafc'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        backgroundColor: 'rgba(100, 181, 246, 0.1)',
                                        padding: '8px',
                                        borderBottom: '1px solid #ccc',
                                    }}
                                >
                                    <DescriptionIcon color="primary" />
                                    <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                        Description
                                    </Typography>
                                </Box>
                                <Box sx={{ padding: '16px' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {capitalizeFirstChar(taskDetails?.description) || 'No Description is added'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    marginBottom: '16px',
                                    mt: 2,
                                    bgcolor: '#f8fafc'
                                }}
                            >
                                <UploadedFilesList />
                            </Box>

                            {/* Subtasks Section - Only shown for Multiple tasks */}
                            {taskDetails?.task_type === 'Multiple' && (
                                <Box sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    marginBottom: '16px',
                                    bgcolor: '#f8fafc'
                                }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            backgroundColor: 'rgba(100, 181, 246, 0.1)',
                                            padding: '8px',
                                            borderBottom: '1px solid #ccc',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PlaylistAddCheckIcon color="primary" />
                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                Sub Tasks ({taskDetails?.subtasks?.length || 0})
                                            </Typography>
                                        </Box>
                                        {isManager && (
                                            <IconButton
                                                size="small"
                                                onClick={handleAddSubtask}
                                                sx={{
                                                    color: 'primary.main',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                                    }
                                                }}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <TableContainer>
                                        <Table aria-label="sub tasks">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Subtask Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Work Type</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Assignee</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Review</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody sx={{ '& > tr': { borderBottom: '1px solid rgba(224, 224, 224, 1)' } }}>
                                                {taskDetails.subtasks && taskDetails.subtasks.length > 0 ? (
                                                    taskDetails.subtasks.map((subTask, index) => (
                                                        <TableRow
                                                            key={subTask.sub_task_id}
                                                            sx={{
                                                                backgroundColor: index % 2 === 0 ? stripedRowColors.even : stripedRowColors.odd,
                                                                '&:last-child td, &:last-child th': { borderBottom: 0 },
                                                                cursor: 'pointer',
                                                                paddingX: 1,
                                                            }}
                                                        >
                                                            <TableCell
                                                                component="th"
                                                                scope="row"
                                                                onClick={() => handleSubTaskDetails(subTask.sub_task_id)}
                                                            >
                                                                <Typography sx={{ fontWeight: 500 }}>
                                                                    {subTask.task_title}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={subTask.work_type || 'Development'}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: getWorkTypeColor(subTask.work_type),
                                                                        color: 'white',
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={subTask.status}
                                                                    color={statusColors[subTask.status] || 'default'}
                                                                    sx={{
                                                                        color: statusTextColors[subTask.status] || statusTextColors.default,
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {subTask.assignees && subTask.assignees.length > 0 ? (
                                                                    subTask.assignees.map((assignee, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={`${capitalizeFirstChar(assignee.first_name)} ${capitalizeFirstChar(assignee.last_name)}`}
                                                                            size="small"
                                                                            sx={{
                                                                                marginRight: 0.5,
                                                                                marginTop: 0.5,
                                                                                fontSize: '0.7rem'
                                                                            }}
                                                                        />
                                                                    ))
                                                                ) : (
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        Not Assigned
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <IconButton size="small">
                                                                    <CheckCircleIcon style={{
                                                                        color: subTask.submit_for_review ? '#FFBF00' : 'grey',
                                                                        fontSize: '18px'
                                                                    }} />
                                                                </IconButton>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                    <Tooltip title="View">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleSubTaskDetails(subTask.sub_task_id)}
                                                                        >
                                                                            <VisibilityIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    {isManager && (
                                                                        <Tooltip title="Delete">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleDeleteSubtask(subTask.sub_task_id)}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center">
                                                            <Typography variant="body2" color="textSecondary">
                                                                No subtasks yet. Add one using the button above.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            <Box sx={{ mt: 4, mb: 2 }}>
                                <Tabs value={value} onChange={handleChange}>
                                    <Tab label={`${taskDetails?.comments?.length || 0} Comments`} />
                                    <Tab label="Activities" />
                                </Tabs>
                                <Box sx={{ mt: 2 }}>
                                    {value === 0 ? (
                                        <Box>
                                            <TextField
                                                fullWidth
                                                placeholder="Type a new comment here"
                                                variant="outlined"
                                                size="small"
                                                value={updatedTaskDetails.comment || ''}
                                                onChange={(e) => setUpdatedTaskDetails(prev => ({ ...prev, comment: e.target.value }))}
                                            />

                                            <Box sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                                                <Table size="small" aria-label="comments table">
                                                    <TableBody>
                                                        {taskDetails.comments && taskDetails.comments.map((comment) => (
                                                            <StripedTableRow key={comment.id}>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>{capitalizeFirstChar(comment.comment)}</TableCell>
                                                                <TableCell sx={{ width: 50 }}>
                                                                    <Avatar sx={{
                                                                        width: 18, height: 18, fontSize: '0.75rem',
                                                                        backgroundColor: comment.user_type === 'Manager' ? '#1976d2' : '#4caf50',
                                                                        color: '#ffffff',
                                                                    }}
                                                                    >
                                                                        {comment.user_type === 'Manager' ? 'M' : 'E'}
                                                                    </Avatar>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {capitalizeFirstChar(comment.user?.first_name)} {capitalizeFirstChar(comment.user?.last_name)}
                                                                    </Typography>
                                                                </TableCell>

                                                                <TableCell align="right" sx={{ width: 120 }}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {moment(comment.createdAt).format('YYYY-MM-DD HH:mm')}
                                                                    </Typography>
                                                                </TableCell>
                                                            </StripedTableRow>
                                                        ))}
                                                        {(!taskDetails.comments || taskDetails.comments.length === 0) && (
                                                            <TableRow>
                                                                <TableCell colSpan={4} align="center">No comments yet.</TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ mt: 1 }}>
                                            {/* Activities content would go here */}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    {/* Right Sidebar */}
                    <Box sx={{ ml: 2, width: 400 }}>
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2, mb: 2 }}>
                                <Chip
                                    label={updatedTaskDetails?.status || taskDetails?.status || 'Not Set'}
                                    color={statusColors[updatedTaskDetails?.status || taskDetails?.status] || 'default'}
                                    size="medium"
                                    variant="contained"
                                    sx={{
                                        padding: '4px',
                                        fontWeight: 'bold',
                                        color: statusTextColors[updatedTaskDetails?.status || taskDetails?.status] || statusTextColors.default
                                    }}
                                    onClick={isManager ? openStatusMenu : undefined}
                                />

                                <Menu
                                    anchorEl={statusAnchorEl}
                                    open={Boolean(statusAnchorEl)}
                                    onClose={() => setStatusAnchorEl(null)}
                                >
                                    <MenuItem onClick={() => {
                                        setUpdatedTaskDetails(prev => ({ ...prev, status: 'In Progress' }));
                                        setStatusAnchorEl(null);
                                    }}>In Progress</MenuItem>

                                    <MenuItem onClick={() => {
                                        setUpdatedTaskDetails(prev => ({ ...prev, status: 'Completed' }));
                                        setStatusAnchorEl(null);
                                    }}>Completed</MenuItem>

                                    <MenuItem onClick={() => {
                                        setUpdatedTaskDetails(prev => ({ ...prev, status: 'Re Open' }));
                                        setStatusAnchorEl(null);
                                    }}>Re-open</MenuItem>

                                    <MenuItem onClick={() => {
                                        setUpdatedTaskDetails(prev => ({ ...prev, status: 'Hold' }));
                                        setStatusAnchorEl(null);
                                    }}>Hold</MenuItem>
                                </Menu>
                                <Chip
                                    label={updatedTaskDetails?.priority || taskDetails?.priority || 'Not Set'}
                                    color={priorityColors[updatedTaskDetails?.priority || taskDetails?.priority] || 'default'}
                                    size="medium"
                                    variant="contained"
                                    sx={{ padding: '4px' }}
                                    onClick={isManager ? openPriorityMenu : undefined}
                                >
                                    {updatedTaskDetails?.priority || taskDetails?.priority || 'Not Set'}
                                </Chip>
                                <Menu
                                    anchorEl={priorityAnchorEl}
                                    open={Boolean(priorityAnchorEl)}
                                    onClose={() => setPriorityAnchorEl(null)}
                                >
                                    <MenuItem onClick={() => {
                                        setUpdatedTaskDetails(prev => ({ ...prev, priority: 'High' }));
                                        setPriorityAnchorEl(null);
                                    }}>High</MenuItem>
                                    <MenuItem onClick={() => {
                                        setUpdatedTaskDetails(prev => ({ ...prev, priority: 'Medium' }));
                                        setPriorityAnchorEl(null);
                                    }}>Medium</MenuItem>
                                    <MenuItem onClick={() => {
                                        setUpdatedTaskDetails(prev => ({ ...prev, priority: 'Low' }));
                                        setPriorityAnchorEl(null);
                                    }}>Low</MenuItem>
                                </Menu>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                                {isManager && (
                                    <TextField
                                        placeholder="0"
                                        fullWidth
                                        inputProps={{ min: 0, max: 100 }}
                                        variant="outlined"
                                        label="Progress Percentage"
                                        type="number"
                                        size="small"
                                        value={updatedTaskDetails.progress !== undefined ? updatedTaskDetails.progress : taskDetails.progress || ''}
                                        onChange={(e) => {
                                            const rawValue = e.target.value;
                                            setUpdatedTaskDetails(prev => ({ ...prev, progress: rawValue }));
                                        }}
                                        onBlur={(e) => {
                                            const value = e.target.value;
                                            if (value === '') {
                                                setUpdatedTaskDetails(prev => ({ ...prev, progress: '' }));
                                            } else {
                                                const parsedValue = parseInt(value, 10);
                                                if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100) {
                                                    setUpdatedTaskDetails(prev => ({ ...prev, progress: prev.progress !== undefined ? prev.progress : taskDetails.progress || '' }));
                                                } else {
                                                    setUpdatedTaskDetails(prev => ({ ...prev, progress: parsedValue }));
                                                }
                                            }
                                        }}
                                    />
                                )}
                                <LinearProgress
                                    variant="determinate"
                                    value={parseInt(updatedTaskDetails.progress !== undefined ? updatedTaskDetails.progress : taskDetails.progress || 0)}
                                    sx={{ mt: 1, borderRadius: 4, height: 10 }}
                                />
                                <Box display="flex" justifyContent="space-between" mt={0.5}>
                                    <Typography variant="caption">0%</Typography>
                                    <Typography variant="caption">100%</Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#666', mb: 1, mt: 2 }}>
                                ASSIGNED TO
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                                {taskDetails?.assignees?.map((assignee) => (
                                    <Box key={`${assignee.first_name}-${assignee.last_name}`} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar
                                            sx={{ width: 32, height: 32, bgcolor: '#b48ead' }}
                                        >
                                            {`${assignee.first_name.charAt(0)}`.toUpperCase()}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ ml: 2 }}>
                                            {`${capitalizeFirstChar(assignee.first_name)} ${capitalizeFirstChar(assignee.last_name)}`}
                                        </Typography>
                                    </Box>
                                ))}
                                {taskDetails?.assignees?.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">
                                        Not Assigned
                                    </Typography>
                                )}
                            </Box>
                            {isManager && (
                                <Button
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    sx={{ color: '#2e86de', textTransform: 'none' }}
                                >
                                    Add assigned
                                </Button>
                            )}

                            {/* Attachments section */}
                            {!isManager && (
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Attachments
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AttachFileIcon />}
                                            onClick={() => fileInputRef.current.click()}
                                            size="small"
                                        >
                                            Add Files
                                        </Button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            onChange={handleFileUpload}
                                            style={{ display: 'none' }}
                                        />
                                    </Box>

                                    {attachments.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Files to be uploaded:
                                            </Typography>
                                            <Grid container spacing={1} sx={{ mt: 1 }}>
                                                {attachments.map((attachment) => {
                                                    const lastDotIndex = attachment.name.lastIndexOf('.');
                                                    const fileName = lastDotIndex > 0 ? attachment.name.substring(0, lastDotIndex) : attachment.name;
                                                    const fileExtension = lastDotIndex > 0 ? attachment.name.substring(lastDotIndex) : '';

                                                    return (
                                                        <Grid item key={attachment.id} xs={12}>
                                                            <Paper
                                                                elevation={1}
                                                                sx={{
                                                                    p: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    backgroundColor: 'background.paper',
                                                                    '&:hover': {
                                                                        bgcolor: 'action.hover',
                                                                    }
                                                                }}
                                                            >
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    minWidth: 0,
                                                                    flexGrow: 1,
                                                                    pr: 1
                                                                }}>
                                                                    {renderFileIcon(attachment.type)}
                                                                    <Box sx={{
                                                                        ml: 1,
                                                                        minWidth: 0,
                                                                        overflow: 'hidden'
                                                                    }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            noWrap
                                                                            sx={{
                                                                                display: 'inline'
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    display: 'inline-block',
                                                                                    maxWidth: 'calc(100% - 30px)',
                                                                                    textOverflow: 'ellipsis',
                                                                                    overflow: 'hidden',
                                                                                    whiteSpace: 'nowrap',
                                                                                    verticalAlign: 'bottom'
                                                                                }}
                                                                            >
                                                                                {fileName}
                                                                            </Box>
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    color: 'text.secondary',
                                                                                    ml: 0.5,
                                                                                    flexShrink: 0
                                                                                }}
                                                                            >
                                                                                {fileExtension}
                                                                            </Box>
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteAttachment(attachment.id);
                                                                    }}
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        '&:hover': {
                                                                            color: 'error.main',
                                                                            backgroundColor: 'transparent'
                                                                        }
                                                                    }}
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </Paper>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            <Dialog
                                open={openPreview}
                                onClose={handleClosePreview}
                                maxWidth="md"
                                fullWidth
                            >
                                <DialogTitle>{previewFile?.name}</DialogTitle>
                                <DialogContent sx={{ position: 'relative', minHeight: '400px' }}>
                                    {previewFile && (
                                        <>
                                            <IconButton
                                                onClick={handleClosePreview}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: 8,
                                                    color: 'grey.500',
                                                    bgcolor: 'background.paper',
                                                    zIndex: 1,
                                                    boxShadow: 1
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                            {previewFile.type.startsWith('image/') ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                                                    <img
                                                        src={previewFile.url}
                                                        alt={previewFile.name}
                                                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    textAlign: 'center'
                                                }}>
                                                    <Box sx={{ fontSize: 60, mb: 2 }}>
                                                        {renderFileIcon(previewFile.type)}
                                                    </Box>
                                                    <Typography variant="h6" gutterBottom>
                                                        {previewFile.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                        This file type cannot be previewed. Please download to view.
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        href={previewFile.url}
                                                        download={previewFile.name}
                                                        startIcon={<CloudDownloadIcon />}
                                                    >
                                                        Download File
                                                    </Button>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </DialogContent>
                            </Dialog>

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                                WATCHERS
                            </Typography>
                            {isManager && (
                                <Button
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    sx={{ color: '#2e86de', textTransform: 'none' }}
                                >
                                    Add watchers
                                </Button>
                            )}
                            <Button
                                variant="text"
                                startIcon={<VisibilityIcon />}
                                sx={{ color: '#2e86de', textTransform: 'none', ml: 2 }}
                            >
                                Watch
                            </Button>
                        </Paper>
                        {taskApiMessage && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: taskApiMessage.includes('success') ? 'success.main' : 'error.main',
                                    fontWeight: 600
                                }}
                            >
                                {taskApiMessage}
                            </Typography>
                        )}
                        {isManager && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 2 }}>
                                <Button variant="contained" onClick={handleSaveUpdateDetailsButton}>
                                    Save
                                </Button>
                            </Box>
                        )}
                        {!isManager && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 2 }}>
                                <Button variant="contained" onClick={handleSendForReviewButton}>
                                    Send For Review
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Add Subtask Modal */}
            {isCreateTaskOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 100,
                    }}
                >
                    <Box sx={{ maxWidth: '90%', maxHeight: '90%', overflow: 'auto' }}>
                        <CreateTask
                            onClose={handleCloseCreateTask}
                            isSubtaskMode={isCreatingSubtask}
                            parentTaskId={taskDetails?.task_id}
                            projectId={taskDetails?.project_id} // You'll need to add this to your task details
                        />
                    </Box>
                </Box>
            )}


            {/* Edit Actual Time Dialog */}
            <Dialog open={openEditModal} onClose={handleCloseEditModal} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Actual Time</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'flex-start' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Hours"
                            type="tel"
                            fullWidth
                            value={actualTimeEdit.hours}
                            onChange={handleActualTimeHoursChange}
                            inputProps={{ maxLength: 3 }}
                        />
                        <TextField
                            margin="dense"
                            label="Minutes"
                            type="tel"
                            fullWidth
                            value={actualTimeEdit.minutes}
                            onChange={handleActualTimeMinutesChange}
                            error={minutesError}
                            helperText={minutesError ? "Minutes should be 0-59" : ""}
                            inputProps={{ maxLength: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditModal}>Cancel</Button>
                    <Button onClick={handleSaveActualTime} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-confirmation-dialog"
            >
                <DialogTitle id="delete-confirmation-dialog">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this task? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>


            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>


        </Box>
    );
};

export default TaskDetailsDummy;